// WHICH convorcher this build is talking to, and the link to its chat UI.
//
// A deployed build connects SAME-ORIGIN — `wss://<page-host>/ws`, forwarded by
// nginx to the runtime ${CONVORCHER_URL} — so the backend's identity appears
// nowhere the browser can see it. That is deliberate (ENG-1009: the image bakes
// no environment), and it has a cost: sessions live in ONE convorcher process,
// so a ?session_id= shared with a client on a DIFFERENT deployment can never
// meet it, and nothing on screen says which deployment you are on. A staging
// avatar and a production convorcher chat tab failed to reach each other for a
// morning for exactly this reason, with a green "Connected" dot on both.
//
// So: `location = /convorcher-target` in nginx.conf.template reports the value
// as a readout, and this module turns it into the two things a person needs —
// a boot line naming the backend, and a link to THIS deployment's own chat UI
// with the live session already in it.
//
// Pure + env-injected, like convorcherTargets.js next door, so it is testable
// under plain node. The fetching half is `loadConvorcherTarget`, the only
// impure function here, and it FAILS LOUDLY (returns null, logs) rather than
// guessing a hostname — a link to the wrong convorcher is the exact defect this
// exists to prevent, and a plausible-looking wrong one is worse than none.

import { resolveConvorcherApiTarget } from './convorcherTargets.js'

/** Path the deploy's nginx serves the readout on. */
export const CONVORCHER_TARGET_PATH = '/convorcher-target'

/**
 * The convorcher origin this build talks to, when it is knowable WITHOUT a
 * fetch. In dev that is the API target the proxy was configured with at
 * startup; deployed it is unknowable here (the runtime env lives in nginx),
 * so this returns null and `loadConvorcherTarget` asks the server.
 */
export function knownConvorcherTarget(env = {}, { dev = false } = {}) {
  return dev ? resolveConvorcherApiTarget(env) : null
}

/**
 * Parse the readout body. A malformed or absent value yields null — an older
 * deployment (or a dev server with no such route) simply cannot answer, and
 * that is reported as "unknown", never as a guess.
 */
export function parseConvorcherTarget(body) {
  const raw = typeof body === 'string' ? safeParse(body) : body
  const url = typeof raw?.convorcherUrl === 'string' ? raw.convorcherUrl.trim() : ''
  if (!url) return null
  try {
    const parsed = new URL(url)
    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return null
    return parsed.origin
  } catch {
    return null
  }
}

/**
 * The convorcher chat URL for a live session.
 *
 * Every param that would make the chat client fork instead of join is carried
 * over — character above all, since convorcher compares it when deciding
 * whether a ?session_id= joins or starts a new session. Omitting it is what
 * made "open the chat on my session" silently produce a second, separate
 * session (fixed server-side too, but a link that states the character is
 * correct against both the old and new server).
 *
 * Returns null when the target or the session is unknown: an incomplete link
 * would land someone on a fresh session that looks like theirs.
 */
export function buildConvorcherChatUrl({ target, sessionId, character, story, features } = {}) {
  const origin = parseConvorcherTarget({ convorcherUrl: target })
  if (!origin || !sessionId) return null

  const params = new URLSearchParams({ session_id: sessionId })
  if (character) params.set('character', character)
  if (story) params.set('story', story)
  if (features) params.set('features', features)
  return `${origin}/?${params}`
}

/**
 * Ask the deployment which convorcher it forwards to. Cached for the page's
 * lifetime — it cannot change without a redeploy, which reloads the page.
 */
let cached
export async function loadConvorcherTarget({ fetchImpl = globalThis.fetch, env = {}, dev = false } = {}) {
  const known = knownConvorcherTarget(env, { dev })
  if (known) return parseConvorcherTarget({ convorcherUrl: known })
  if (cached !== undefined) return cached

  try {
    const res = await fetchImpl(CONVORCHER_TARGET_PATH, { cache: 'no-store' })
    // The SPA fallback answers unknown paths with index.html, so a deployment
    // that predates the route returns 200 text/html. Parsing decides, not the
    // status code.
    cached = res.ok ? parseConvorcherTarget(await res.text()) : null
  } catch {
    cached = null
  }
  if (!cached) {
    console.warn(
      `[server] convorcher: unknown — ${CONVORCHER_TARGET_PATH} did not answer with one. ` +
        'This deployment predates the readout, or nginx has no CONVORCHER_URL.',
    )
  }
  return cached
}

/** Test seam: forget the cached readout. */
export function _resetConvorcherTargetCache() {
  cached = undefined
}

function safeParse(text) {
  try {
    return JSON.parse(text)
  } catch {
    return null
  }
}
